import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import LegalNotice from '../../components/LegalNotice';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const hasRecoveryParams = () => {
  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const searchParams = new URLSearchParams(window.location.search);

  return hashParams.get('type') === 'recovery'
    || searchParams.get('type') === 'recovery'
    || hashParams.has('access_token')
    || searchParams.has('code');
};

const ResetPassword = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [canResetPassword, setCanResetPassword] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');

  useEffect(() => {
    let mounted = true;
    let recoveryDetected = false;
    const cameFromRecoveryLink = hasRecoveryParams();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;

      if (event === 'PASSWORD_RECOVERY') {
        recoveryDetected = true;
        setCanResetPassword(true);
        setError(null);
        setMessage('Enter a new password for your account.');
      } else if (event === 'SIGNED_IN' && cameFromRecoveryLink && session) {
        recoveryDetected = true;
        setCanResetPassword(true);
        setError(null);
      }
    });

    const checkSession = async () => {
      try {
        const { data, error: sessionError } = await supabase.auth.getSession();
        if (!mounted) return;

        if (sessionError) throw sessionError;

        if (data.session && (cameFromRecoveryLink || recoveryDetected)) {
          setCanResetPassword(true);
          setMessage('Enter a new password for your account.');
        } else {
          setCanResetPassword(false);
          setError('This password reset link is invalid, expired, or has already been used. Please request a new password reset email.');
        }
      } catch (err) {
        if (!mounted) return;
        setCanResetPassword(false);
        setError(err.message || 'Unable to validate this password reset link. Please request a new one.');
      } finally {
        if (mounted) setCheckingSession(false);
      }
    };

    const timeout = window.setTimeout(checkSession, 400);

    return () => {
      mounted = false;
      window.clearTimeout(timeout);
      subscription.unsubscribe();
    };
  }, []);

  const validatePassword = () => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long.';
    }

    if (password !== confirmPassword) {
      return 'Passwords do not match.';
    }

    return null;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setMessage('');

    const validationError = validatePassword();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setLoading(true);
      const { error: updateError } = await supabase.auth.updateUser({
        password
      });

      if (updateError) throw updateError;

      setSuccess(true);
      setPassword('');
      setConfirmPassword('');
      setMessage('Your password has been updated. You can continue to your dashboard or sign in again.');
    } catch (err) {
      console.error('Password reset update error:', err);
      setError(err.message || 'Unable to update your password. Please request a new reset link and try again.');
    } finally {
      setLoading(false);
    }
  };

  const returnToLogin = async () => {
    await supabase.auth.signOut();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col justify-between">
      <div className="flex-1 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center">
            <SafeIcon icon={success ? FiIcons.FiCheckCircle : FiIcons.FiLock} className="h-12 w-12 text-slate-800" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-slate-900">
            Reset Your Password
          </h2>
          <p className="mt-2 text-center text-sm text-slate-600">
            Choose a new password for your Trustworthy Estate Planning account.
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 border border-slate-200">
            {checkingSession ? (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <SafeIcon icon={FiIcons.FiLoader} className="h-8 w-8 animate-spin text-slate-300" />
                <p className="mt-4 text-sm text-slate-500">Checking your reset link...</p>
              </div>
            ) : (
              <>
                {error && (
                  <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
                    <div className="flex">
                      <SafeIcon icon={FiIcons.FiAlertCircle} className="h-5 w-5 text-red-400 mr-2 shrink-0" />
                      <p className="text-sm text-red-700 font-medium">{error}</p>
                    </div>
                  </div>
                )}

                {message && (
                  <div className={`mb-6 border-l-4 p-4 rounded-md ${success ? 'bg-green-50 border-green-400' : 'bg-blue-50 border-blue-400'}`}>
                    <div className="flex">
                      <SafeIcon icon={success ? FiIcons.FiCheckCircle : FiIcons.FiInfo} className={`h-5 w-5 mr-2 shrink-0 ${success ? 'text-green-500' : 'text-blue-500'}`} />
                      <p className={`text-sm font-medium ${success ? 'text-green-700' : 'text-blue-700'}`}>{message}</p>
                    </div>
                  </div>
                )}

                {canResetPassword && !success ? (
                  <form className="space-y-6" onSubmit={handleSubmit}>
                    <div>
                      <label htmlFor="password" className="block text-sm font-medium text-slate-700">
                        New password
                      </label>
                      <input
                        id="password"
                        name="password"
                        type="password"
                        required
                        minLength={8}
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        className="mt-1 focus:ring-slate-500 focus:border-slate-500 block w-full sm:text-sm border-slate-300 rounded-md py-2 px-3 border shadow-sm"
                        autoComplete="new-password"
                      />
                      <p className="mt-1 text-xs text-slate-500">Use at least 8 characters.</p>
                    </div>

                    <div>
                      <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-700">
                        Confirm new password
                      </label>
                      <input
                        id="confirmPassword"
                        name="confirmPassword"
                        type="password"
                        required
                        minLength={8}
                        value={confirmPassword}
                        onChange={(event) => setConfirmPassword(event.target.value)}
                        className="mt-1 focus:ring-slate-500 focus:border-slate-500 block w-full sm:text-sm border-slate-300 rounded-md py-2 px-3 border shadow-sm"
                        autoComplete="new-password"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-slate-800 hover:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-900 disabled:opacity-50 transition-colors"
                    >
                      {loading ? (
                        <span className="flex items-center">
                          <SafeIcon icon={FiIcons.FiLoader} className="animate-spin mr-2 h-4 w-4" />
                          Updating Password...
                        </span>
                      ) : 'Update Password'}
                    </button>
                  </form>
                ) : (
                  <div className="space-y-3">
                    {success && (
                      <button
                        type="button"
                        onClick={() => navigate('/dashboard', { replace: true })}
                        className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-slate-800 hover:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-900 transition-colors"
                      >
                        Continue to Dashboard
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={returnToLogin}
                      className="w-full flex justify-center py-2 px-4 border border-slate-200 rounded-md shadow-sm text-sm font-medium text-slate-700 bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-400 transition-colors"
                    >
                      Return to Login
                    </button>
                  </div>
                )}

                {!success && (
                  <div className="mt-6 text-center">
                    <Link to="/forgot-password" className="font-medium text-slate-600 hover:text-slate-900 text-sm">
                      Request a new reset link
                    </Link>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <LegalNotice />
    </div>
  );
};

export default ResetPassword;
